/**
 * Product page enhancements.
 *
 * Desktop split-scroll gallery:
 * The photo stack (.Product__Slideshow) scrolls independently inside the
 * gallery column (see assets/product-page.css, .Product__Wrapper--splitScroll).
 * Clicking a thumbnail or dot must scroll that stack. The theme's generic
 * anchor handler scrolls the window instead, which lands in the wrong place,
 * so we intercept those clicks in the capture phase (before the theme's
 * document-level delegate) and scroll the stack ourselves.
 *
 * Variant changes also funnel through here: theme.js simulates a click on the
 * matching thumbnail when a variant with a featured image is selected.
 */
(function () {
  'use strict';

  // Touch devices keep the standard layout
  if (window.matchMedia && window.matchMedia('(-moz-touch-enabled: 1), (hover: none)').matches) {
    return;
  }

  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) {
      return;
    }

    var link = event.target.closest('.Product__Wrapper--splitScroll [href^="#Media"]');

    if (!link) {
      return;
    }

    var gallery = link.closest('.Product__Gallery--stack');
    var scroller = gallery && gallery.querySelector('.Product__Slideshow');

    if (!scroller) {
      return;
    }

    // Below the desktop breakpoint the stack is not a scroll container, and
    // the theme's regular handling is the right one. Split scrolling is
    // flagged by a custom property in the CSS rule that enables it.
    if (window.getComputedStyle(gallery).getPropertyValue('--split-scroll').trim() !== '1') {
      return;
    }

    var media = document.getElementById(link.getAttribute('href').slice(1));

    if (!media) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (link.hasAttribute('data-focus-on-click')) {
      media.focus({ preventScroll: true });
    }

    // The highlight sync below keeps this media selected while it is in
    // view; the stack may be too tall to bring it to the very top
    scroller.setAttribute('data-split-target', media.id);

    scroller.scrollTo({
      top: media.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop,
      behavior: 'smooth'
    });

    // A stack that shows every photo cannot scroll, so no scroll event
    // would refresh the highlight; ask the highlight sync below directly
    scroller.dispatchEvent(new CustomEvent('split:highlight', { bubbles: true }));
  }, true);
}());

/**
 * Desktop split-scroll gallery: keep the thumbnail/dot highlight in sync.
 *
 * The theme highlights the current image with a ScrollSpy: an
 * IntersectionObserver against the window plus a stateful list of which
 * images intersect it, tuned for the stock layout where the images scroll
 * with the page. With the photos scrolling in their own container, the fast
 * enter/leave bursts of an inner scroll get that list out of order and the
 * wrong thumbnail stays outlined. While split scrolling is active, swallow
 * the spy's event before it reaches the section (its only consumer sets the
 * highlight classes — nothing else listens) and derive the current image
 * from the stack's own scroll position instead: a photo just brought in by
 * a thumbnail click while it stays in view, otherwise the photo under the
 * middle of the stack's scrollport, snapped to the first/last photo at the
 * scroll extremes so the edges are always reachable.
 */
(function () {
  'use strict';

  function isSplitScrolling(gallery) {
    // Flagged by the CSS rule itself
    return !!gallery && window.getComputedStyle(gallery).getPropertyValue('--split-scroll').trim() === '1';
  }

  document.addEventListener('scrollspy:target:changed', function (event) {
    if (!event.target || !event.target.querySelector) {
      return;
    }

    var gallery = event.target.querySelector('.Product__Wrapper--splitScroll .Product__Gallery--stack');

    if (isSplitScrolling(gallery)) {
      event.stopPropagation();
    }
  }, true);

  function setSelected(items, index) {
    // Thumbnails and dots are index-aligned with data-media-position - 1,
    // the same mapping the theme's own handler uses
    for (var i = 0; i < items.length; i++) {
      if (i === index) {
        items[i].classList.add('is-selected');
      } else {
        items[i].classList.remove('is-selected');
      }
    }
  }

  function updateHighlight(gallery) {
    if (!isSplitScrolling(gallery)) {
      return;
    }

    var scroller = gallery.querySelector('.Product__Slideshow');

    if (!scroller) {
      return;
    }

    var slides = scroller.querySelectorAll('.Product__SlideItem');

    if (slides.length < 2) {
      return;
    }

    var current;
    var bounds = scroller.getBoundingClientRect();
    var targetId = scroller.getAttribute('data-split-target');
    var target = targetId ? document.getElementById(targetId) : null;

    if (target) {
      var targetBounds = target.getBoundingClientRect();

      // A thumbnail was clicked: keep that photo highlighted as long as it is
      // fully in view (a tall stack can show several photos at once)
      if (targetBounds.top >= bounds.top - 1 && targetBounds.bottom <= bounds.bottom + 1) {
        current = target;
      } else {
        scroller.removeAttribute('data-split-target');
      }
    }

    if (current) {
      // keep the clicked photo
    } else if (scroller.scrollTop <= 1) {
      current = slides[0];
    } else if (scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1) {
      current = slides[slides.length - 1];
    } else {
      var line = bounds.top + scroller.clientHeight / 2;
      current = slides[0];

      for (var i = 1; i < slides.length; i++) {
        if (slides[i].getBoundingClientRect().top <= line) {
          current = slides[i];
        } else {
          break;
        }
      }
    }

    var index = parseInt(current.getAttribute('data-media-position'), 10) - 1;
    var section = gallery.closest('[data-section-type="product"]');

    if (!section || isNaN(index) || index < 0) {
      return;
    }

    setSelected(section.querySelectorAll('.Product__SlideshowNav--thumbnails .Product__SlideshowNavImage'), index);
    setSelected(section.querySelectorAll('.Product__SlideshowNav--dots a'), index);
  }

  var pendingGallery = null;

  // The user taking over the wheel ends the clicked-thumbnail preference
  document.addEventListener('wheel', function (event) {
    var scroller = event.target && event.target.closest && event.target.closest('.Product__Slideshow[data-split-target]');

    if (scroller) {
      scroller.removeAttribute('data-split-target');
    }
  }, { capture: true, passive: true });

  document.addEventListener('scroll', function (event) {
    var target = event.target;

    if (!target || target.nodeType !== 1 || !target.classList.contains('Product__Slideshow')) {
      return;
    }

    var gallery = target.closest('.Product__Gallery--stack');

    if (gallery && !pendingGallery) {
      pendingGallery = gallery;
      window.requestAnimationFrame(function () {
        var pending = pendingGallery;
        pendingGallery = null;
        updateHighlight(pending);
      });
    }
  }, true);

  document.addEventListener('split:highlight', function (event) {
    var gallery = event.target && event.target.closest && event.target.closest('.Product__Gallery--stack');

    if (gallery) {
      updateHighlight(gallery);
    }
  });

  function refreshAll() {
    var galleries = document.querySelectorAll('.Product__Wrapper--splitScroll .Product__Gallery--stack');

    for (var i = 0; i < galleries.length; i++) {
      updateHighlight(galleries[i]);
    }
  }

  refreshAll();
  window.addEventListener('resize', refreshAll);
  document.addEventListener('shopify:section:load', refreshAll);
}());

/**
 * Desktop split-scroll gallery: publish the first photo's height, and flag
 * a photo stack that has nothing left to scroll.
 *
 * The gallery's min-height in product-page.css is the taller of one screen
 * and the first photo, so at least one full photo is always visible even
 * when the description is short and the photo is a tall portrait. CSS can't
 * measure the photo, so it is written here as --first-slide-height. The
 * slides reserve their height with an aspect-ratio box, so the value is
 * right before the image itself has loaded.
 *
 * When every photo already fits (long description, few photos) the stack
 * has nothing to scroll, and the wheel must reach the page instead of dying
 * on the photo: data-split-fits switches the CSS to that mode.
 */
(function () {
  'use strict';

  if (window.matchMedia && window.matchMedia('(-moz-touch-enabled: 1), (hover: none)').matches) {
    return;
  }

  function update() {
    var galleries = document.querySelectorAll('.Product__Wrapper--splitScroll .Product__Gallery--stack');

    for (var i = 0; i < galleries.length; i++) {
      var scroller = galleries[i].querySelector('.Product__Slideshow');
      var slide = galleries[i].querySelector('.Product__SlideItem');

      if (slide) {
        galleries[i].style.setProperty('--first-slide-height', Math.ceil(slide.getBoundingClientRect().height) + 'px');
      }

      if (!scroller) {
        continue;
      }

      if (scroller.scrollHeight <= scroller.clientHeight + 1) {
        scroller.setAttribute('data-split-fits', '');
      } else {
        scroller.removeAttribute('data-split-fits');
      }
    }
  }

  var queued = false;

  function queueUpdate() {
    if (!queued) {
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        update();
      });
    }
  }

  function observeAll() {
    if (!window.ResizeObserver) {
      return;
    }

    var galleries = document.querySelectorAll('.Product__Wrapper--splitScroll .Product__Gallery--stack');

    for (var i = 0; i < galleries.length; i++) {
      if (!galleries[i].hasAttribute('data-split-observed')) {
        galleries[i].setAttribute('data-split-observed', '');
        // The description (hence the gallery height) can change after load:
        // variant changes, apps injecting widgets
        new ResizeObserver(queueUpdate).observe(galleries[i]);
      }
    }
  }

  update();
  observeAll();
  window.addEventListener('load', queueUpdate);
  window.addEventListener('resize', queueUpdate);
  document.addEventListener('shopify:section:load', function () {
    update();
    observeAll();
  });
}());

/**
 * Mobile: tap the product image to open the fullscreen zoom.
 *
 * theme.js already opens PhotoSwipe when the image itself is clicked, but
 * only on desktop — on mobile (below the 1008px lap breakpoint) its
 * _initPhotoSwipeFromImageClick early-returns and only the small round "+"
 * button opens the zoom. Forward a tap on an image slide to that button so
 * the natural gesture works too.
 */
(function () {
  'use strict';

  document.addEventListener('click', function (event) {
    // From 1008px up the theme's own image-click zoom already handles this
    if (!window.matchMedia || !window.matchMedia('(max-width: 1007px)').matches) {
      return;
    }

    // Flickity prevents the click that ends a carousel swipe; a swipe must
    // not open the zoom
    if (event.defaultPrevented) {
      return;
    }

    if (!event.target || !event.target.closest) {
      return;
    }

    var slide = event.target.closest('.Product__SlideItem--image');

    if (!slide) {
      return;
    }

    var section = slide.closest('[data-section-type="product"]');
    var zoomButton = section && section.querySelector('[data-action="open-product-zoom"]');

    // No button means image zoom is disabled in the theme settings
    if (!zoomButton) {
      return;
    }

    zoomButton.click();
  });
}());

/**
 * Touch: pinch the product image to zoom it in place, without first tapping
 * it open.
 *
 * Native pinch-zoom cannot happen on the slideshow: Flickity puts
 * touch-action: pan-y on its viewport, and the viewport meta in
 * theme.liquid caps page zoom at 1. So a two-finger touch on an image slide
 * is handled here instead: a copy of the image is lifted above the page
 * (product-page.css, .ProductPinchZoom) and follows the fingers, then
 * springs back once they lift. A sharper rendition is swapped into the copy
 * while it is zoomed. Tapping the image still opens the fullscreen zoom.
 */
(function () {
  'use strict';

  var MAX_SCALE = 4;
  var SPRING_BACK_MS = 250;

  var gesture = null;
  var suppressClicksUntil = 0;

  function distance(a, b) {
    return Math.sqrt(Math.pow(a.clientX - b.clientX, 2) + Math.pow(a.clientY - b.clientY, 2));
  }

  function midpoint(a, b) {
    return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
  }

  // The image's own {width}x URL at a size that stays sharp at MAX_SCALE,
  // limited to the widths the theme already serves (data-widths)
  function sharpImageUrl(image, displayedWidth) {
    var template = image.getAttribute('data-src');
    var widths;

    try {
      widths = JSON.parse(image.getAttribute('data-widths'));
    } catch (error) {
      return null;
    }

    if (!template || template.indexOf('{width}') === -1 || !widths || !widths.length) {
      return null;
    }

    var wanted = displayedWidth * (window.devicePixelRatio || 1) * MAX_SCALE;
    var maxWidth = parseInt(image.getAttribute('data-max-width')) || Infinity;
    var chosen = null;

    widths.sort(function (a, b) { return a - b; });

    for (var i = 0; i < widths.length; i++) {
      if (widths[i] > maxWidth) {
        break;
      }

      chosen = widths[i];

      if (widths[i] >= wanted) {
        break;
      }
    }

    return chosen ? template.replace('{width}', chosen) : null;
  }

  function start(image, touchA, touchB) {
    var rect = image.getBoundingClientRect();

    if (!rect.width || !rect.height) {
      return;
    }

    var backdrop = document.createElement('div');
    backdrop.className = 'ProductPinchZoom__Backdrop';

    var copy = document.createElement('img');
    copy.className = 'ProductPinchZoom__Image';
    copy.alt = '';
    copy.src = image.currentSrc || image.src;
    copy.style.left = rect.left + 'px';
    copy.style.top = rect.top + 'px';
    copy.style.width = rect.width + 'px';
    copy.style.height = rect.height + 'px';

    document.body.appendChild(backdrop);
    document.body.appendChild(copy);
    image.style.visibility = 'hidden';

    var sharpUrl = sharpImageUrl(image, rect.width);

    if (sharpUrl && sharpUrl !== copy.src) {
      var loader = new Image();

      loader.onload = function () {
        if (gesture && gesture.copy === copy) {
          copy.src = sharpUrl;
        }
      };

      loader.src = sharpUrl;
    }

    var center = midpoint(touchA, touchB);

    gesture = {
      image: image,
      copy: copy,
      backdrop: backdrop,
      rect: rect,
      idA: touchA.identifier,
      idB: touchB.identifier,
      startDistance: Math.max(distance(touchA, touchB), 1),
      // The point of the image under the fingers, in the image's own pixels
      anchorX: center.x - rect.left,
      anchorY: center.y - rect.top,
      pinching: true
    };
  }

  function move(touches) {
    var touchA = null;
    var touchB = null;

    for (var i = 0; i < touches.length; i++) {
      if (touches[i].identifier === gesture.idA) {
        touchA = touches[i];
      } else if (touches[i].identifier === gesture.idB) {
        touchB = touches[i];
      }
    }

    if (!touchA || !touchB) {
      return;
    }

    var scale = Math.min(Math.max(distance(touchA, touchB) / gesture.startDistance, 1), MAX_SCALE);
    var center = midpoint(touchA, touchB);

    // Keep the anchor point under the fingers' midpoint (transform-origin is
    // the top left corner)
    var x = center.x - gesture.rect.left - scale * gesture.anchorX;
    var y = center.y - gesture.rect.top - scale * gesture.anchorY;

    gesture.copy.style.transform = 'translate(' + x + 'px, ' + y + 'px) scale(' + scale + ')';
    gesture.backdrop.style.opacity = Math.min((scale - 1) / 1.5, 1);
  }

  function release() {
    var ending = gesture;

    ending.pinching = false;
    ending.copy.classList.add('is-returning');
    ending.backdrop.classList.add('is-returning');
    ending.copy.style.transform = '';
    ending.backdrop.style.opacity = '';

    setTimeout(function () {
      ending.image.style.visibility = '';
      ending.copy.remove();
      ending.backdrop.remove();
    }, SPRING_BACK_MS);
  }

  document.addEventListener('touchstart', function (event) {
    if (gesture || event.touches.length !== 2) {
      return;
    }

    var target = event.target && event.target.closest && event.target.closest('.Product__SlideItem--image');
    var image = target && target.querySelector('img');

    // Not loaded yet: nothing worth zooming into
    if (!image || !(image.currentSrc || image.src)) {
      return;
    }

    // Stops the page scrolling and the browser's own gesture handling for
    // the rest of this touch sequence
    event.preventDefault();
    start(image, event.touches[0], event.touches[1]);
  }, { passive: false });

  document.addEventListener('touchmove', function (event) {
    if (!gesture) {
      return;
    }

    event.preventDefault();

    if (gesture.pinching) {
      move(event.touches);
    }
  }, { passive: false });

  function onTouchEnd(event) {
    if (!gesture) {
      return;
    }

    if (gesture.pinching && event.touches.length < 2) {
      release();
    }

    // Keep the gesture (and the carousel blocked) until every finger is up,
    // so the finger left behind does not swipe the carousel or scroll
    if (event.touches.length === 0) {
      gesture = null;
      suppressClicksUntil = Date.now() + 400;
    }
  }

  document.addEventListener('touchend', onTouchEnd);
  document.addEventListener('touchcancel', onTouchEnd);

  // Flickity tracks drags with pointer events on window (bubble phase):
  // swallow them while a pinch owns the touch so the carousel stays put. The
  // pointerup/cancel still go through so Flickity's own drag state resets.
  window.addEventListener('pointermove', function (event) {
    if (gesture && event.pointerType === 'touch') {
      event.stopPropagation();
    }
  }, true);

  // A pinch must not end in the tap that opens the fullscreen zoom
  document.addEventListener('click', function (event) {
    if (Date.now() < suppressClicksUntil && event.target && event.target.closest && event.target.closest('.Product__SlideItem--image')) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, true);

  // iOS Safari's own page pinch (it ignores maximum-scale)
  document.addEventListener('gesturestart', function (event) {
    if (gesture) {
      event.preventDefault();
    }
  });
}());

/**
 * Desktop zoom: frosted-glass backdrop.
 *
 * While the zoom is open, theme.css blurs .PageContainer
 * (body:has(.pswp--open) ...). The theme renders the PhotoSwipe root
 * inside the product section — inside .PageContainer — where it would be
 * blurred along with the page, so move it to <body> (where PhotoSwipe
 * expects it anyway). Re-run on section re-renders in the theme editor.
 */
(function () {
  'use strict';

  function relocateZoomRoots() {
    var roots = document.querySelectorAll('.PageContainer .pswp');

    for (var i = 0; i < roots.length; i++) {
      document.body.appendChild(roots[i]);
    }
  }

  relocateZoomRoots();
  document.addEventListener('shopify:section:load', relocateZoomRoots);
}());

/**
 * Keep the zoom's page blur cheap: clip the blurred page to the visible
 * viewport while the zoom is open.
 *
 * theme.css blurs all of .PageContainer while a .pswp--open element
 * exists. Unclipped, the browser rasterizes that blur over the page's
 * entire height; clipping to the viewport (plus margin so the blur kernel
 * has real pixels to sample at the visible edges) keeps it to about one
 * screen. PhotoSwipe closes on page scroll, so the clip cannot go stale
 * while open.
 */
(function () {
  'use strict';

  // Comfortably more than the 28px blur radius in theme.css
  var CLIP_MARGIN = 120;

  function updateClip() {
    var container = document.querySelector('.PageContainer');

    if (!container) {
      return;
    }

    if (!document.querySelector('.pswp--open')) {
      container.style.clipPath = '';
      return;
    }

    var rect = container.getBoundingClientRect();
    var top = Math.max(0, -rect.top - CLIP_MARGIN);
    var bottom = Math.max(0, rect.bottom - window.innerHeight - CLIP_MARGIN);

    container.style.clipPath = 'inset(' + top + 'px 0px ' + bottom + 'px 0px)';
  }

  function watchZoomRoots() {
    var roots = document.querySelectorAll('.pswp');

    for (var i = 0; i < roots.length; i++) {
      if (!roots[i].hasAttribute('data-clip-watched')) {
        roots[i].setAttribute('data-clip-watched', '');
        new MutationObserver(updateClip).observe(roots[i], { attributes: true, attributeFilter: ['class'] });
      }
    }
  }

  watchZoomRoots();
  window.addEventListener('resize', updateClip);
  document.addEventListener('shopify:section:load', watchZoomRoots);
}());

/**
 * Mobile sticky add-to-cart bar.
 *
 * Markup lives at the end of sections/main-product.liquid (data-sticky-atc),
 * styles in product-page.css (phone breakpoint only). The bar slides up once
 * the form's buy buttons have been scrolled above the viewport and hides
 * again when they come back into view.
 *
 * The bar's button forwards its click to the real ProductForm__AddToCart, so
 * variant, quantity, the ajax cart and error handling all remain the
 * theme's. Its label and disabled state are mirrored from the real button
 * (which theme.js rewrites on variant change and while adding), and the
 * price is mirrored from ProductMeta__PriceList, which theme.js updates
 * before dispatching variant:changed.
 */
(function () {
  'use strict';

  function initBar(bar) {
    if (bar.hasAttribute('data-sticky-atc-ready')) {
      return;
    }

    var section = bar.closest('[data-section-type="product"]');
    var realButton = section && section.querySelector('.ProductForm__AddToCart');
    var barButton = bar.querySelector('[data-sticky-atc-button]');

    if (!realButton || !barButton) {
      return;
    }

    bar.setAttribute('data-sticky-atc-ready', '');

    function syncButton() {
      barButton.innerHTML = realButton.innerHTML;
      barButton.disabled = realButton.disabled;
    }

    function syncPrice() {
      var barPrice = bar.querySelector('[data-sticky-atc-price]');
      var priceList = section.querySelector('.ProductMeta__PriceList');

      if (barPrice && priceList) {
        barPrice.innerHTML = priceList.innerHTML;
      }
    }

    syncButton();
    syncPrice();

    new MutationObserver(syncButton).observe(realButton, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled']
    });

    section.addEventListener('variant:changed', syncPrice);

    barButton.addEventListener('click', function () {
      realButton.click();
    });

    // Show only once the buy buttons sit fully above the viewport — not
    // while they are visible, and not before they have been reached. This
    // is a scroll listener rather than an IntersectionObserver on purpose:
    // an observer only fires when the intersection state changes, and a
    // scroll jump that skips clean over the buttons (fast flick, anchor
    // link) leaves them "not intersecting" on both sides — no callback,
    // and the bar would never show.
    var buyButtons = realButton.closest('.ProductForm__BuyButtons') || realButton;
    var phoneMedia = window.matchMedia('(max-width: 640px)');
    var updateQueued = false;

    function updateVisibility() {
      updateQueued = false;

      var scrolledPast = phoneMedia.matches && buyButtons.getBoundingClientRect().bottom < 0;

      bar.classList.toggle('is-visible', scrolledPast);
    }

    function queueUpdate() {
      if (!updateQueued) {
        updateQueued = true;
        window.requestAnimationFrame(updateVisibility);
      }
    }

    window.addEventListener('scroll', queueUpdate, { passive: true });
    window.addEventListener('resize', queueUpdate);
    updateVisibility();
  }

  function initAll() {
    var bars = document.querySelectorAll('[data-sticky-atc]');

    for (var i = 0; i < bars.length; i++) {
      initBar(bars[i]);
    }
  }

  initAll();
  document.addEventListener('shopify:section:load', initAll);
}());
